import React from 'react';
import { faChevronRight, faCircleExclamation, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GovernanceProcessingStatusIcon } from './GovernanceProcessingStatusIcon';
import { BP_CARD_CLASSES } from './brandPresenceClasses';

const LABEL_CLASSES = 'mb-1 font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500';

/** The stripe on a row's leading edge: red for errors, amber for warnings, none otherwise. */
const EDGE_CLASSES: Record<'none' | 'warning' | 'error', string> = {
  none: 'bg-transparent',
  warning: 'bg-amber-400',
  error: 'bg-rose-500',
};

export function GovernanceJobRow({
  processing,
  title,
  titleTooltip,
  subtitle,
  startedAt,
  duration,
  warningCount,
  errorCount,
  leftBorder,
  onClick,
}: {
  processing: boolean;
  title: string;
  titleTooltip?: string;
  subtitle: string;
  startedAt: string;
  duration: string;
  warningCount: number;
  errorCount: number;
  leftBorder: 'none' | 'warning' | 'error';
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex w-full items-center gap-5 overflow-hidden px-5 py-4 text-left ${BP_CARD_CLASSES} transition-all duration-200 hover:-translate-y-px hover:shadow-card-hover hover:ring-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50`}
    >
      <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${EDGE_CLASSES[leftBorder]}`} />

      <div className="flex-shrink-0">
        <GovernanceProcessingStatusIcon processing={processing} />
      </div>

      <div className="grid flex-grow grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-4">
        <div className="min-w-0">
          <h3 className="m-0 truncate text-[0.9375rem] font-semibold text-slate-900" title={titleTooltip}>
            {title}
          </h3>
          <p className="m-0 mt-0.5 truncate text-sm text-slate-500">{subtitle}</p>
        </div>

        <div>
          <p className={LABEL_CLASSES}>Started</p>
          <p className="m-0 text-sm text-slate-700 tabular-nums">{startedAt}</p>
        </div>

        <div>
          <p className={LABEL_CLASSES}>Duration</p>
          <p className={`m-0 text-sm tabular-nums ${processing ? 'font-semibold text-primary' : 'text-slate-700'}`}>{duration}</p>
        </div>

        <div className="flex items-center justify-end gap-2">
          {warningCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-[0.625rem]" />
              <span className="tabular-nums">{warningCount}</span>
            </span>
          ) : null}
          {errorCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200">
              <FontAwesomeIcon icon={faCircleExclamation} className="text-[0.625rem]" />
              <span className="tabular-nums">{errorCount}</span>
            </span>
          ) : null}
          <FontAwesomeIcon
            icon={faChevronRight}
            className="ml-1 text-sm text-slate-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-primary"
          />
        </div>
      </div>
    </button>
  );
}
